import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";

import { PDF_LOAD_OPTIONS } from "@/Utils/pdfLoadOptions";
import { usePdfPageWindow } from "@/Utils/usePdfPageWindow";
import { PdfThumbnailPages, PdfThumbnailRail } from "@/Components/DocumentModal/PdfThumbnail";
import PdfNavigation from "@/Components/DocumentModal/PdfNavigation";

import "@/Utils/pdfWorker";
import "@/Components/DocumentModal/Documents.css";

const FALLBACK_PAGE_SIZE = { width: 612, height: 792 };

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const FaxPdfViewer = ({ file, onLoadError, onRetry }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState(true);
  const [pageSizes, setPageSizes] = useState({});
  const [scrollElement, setScrollElement] = useState(null);
  const [railScrollElement, setRailScrollElement] = useState(null);
  const [railPortalElement, setRailPortalElement] = useState(null);
  const pageVisibilityRef = useRef(new Map());

  const pageNumbers = useMemo(
    () => Array.from({ length: numPages || 0 }, (_, index) => index + 1),
    [numPages]
  );
  const visiblePages = usePdfPageWindow(scrollElement, numPages, {
    selector: ".fax-pdf-page",
    rootMargin: "125% 0px",
  });
  const baselineSize = pageSizes[1] || Object.values(pageSizes)[0] || FALLBACK_PAGE_SIZE;

  const applyFitZoom = (pageSize = baselineSize) => {
    if (!scrollElement || !pageSize?.width || !pageSize?.height) return;
    const availableWidth = Math.max(scrollElement.clientWidth - 28, 200);
    const availableHeight = Math.max(scrollElement.clientHeight - 28, 250);
    setZoom(
      clamp(Math.min(availableWidth / pageSize.width, availableHeight / pageSize.height), 0.2, 2)
    );
  };

  const handleZoomChange = (zoomType) => {
    const pageSize = baselineSize;
    const availableWidth = Math.max((scrollElement?.clientWidth || 0) - 28, 200);
    const availableHeight = Math.max((scrollElement?.clientHeight || 0) - 28, 250);

    if (zoomType === "in" || zoomType === "out") {
      setFitMode(false);
      setZoom((current) => clamp(current + (zoomType === "in" ? 0.1 : -0.1), 0.2, 4));
      return;
    }

    if (zoomType === "width") {
      setFitMode(false);
      setZoom(clamp(availableWidth / pageSize.width, 0.2, 4));
      return;
    }

    if (zoomType === "height") {
      setFitMode(false);
      setZoom(clamp(availableHeight / pageSize.height, 0.2, 4));
      return;
    }

    if (zoomType === "fit") {
      setFitMode(true);
      applyFitZoom(pageSize);
      return;
    }

    if (typeof zoomType === "number") {
      setFitMode(false);
      setZoom(clamp(zoomType, 0.2, 4));
    }
  };

  useEffect(() => {
    setNumPages(0);
    setPageNumber(1);
    setPageSizes({});
    setFitMode(true);
    pageVisibilityRef.current.clear();
  }, [file]);

  useEffect(() => {
    if (!scrollElement || !numPages) return undefined;
    const pageElements = scrollElement.querySelectorAll(".fax-pdf-page");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const page = Number(entry.target.dataset.pageNumber);
          if (entry.isIntersecting) pageVisibilityRef.current.set(page, entry.intersectionRatio);
          else pageVisibilityRef.current.delete(page);
        });

        let mostVisiblePage = pageNumber;
        let greatestRatio = -1;
        pageVisibilityRef.current.forEach((ratio, page) => {
          if (ratio > greatestRatio) {
            greatestRatio = ratio;
            mostVisiblePage = page;
          }
        });
        if (greatestRatio >= 0) setPageNumber(mostVisiblePage);
      },
      { root: scrollElement, threshold: [0.1, 0.35, 0.6, 0.85] }
    );

    pageElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [numPages, pageNumber, scrollElement]);

  useEffect(() => {
    if (!scrollElement || !fitMode) return undefined;
    const observer = new ResizeObserver(() => applyFitZoom());
    observer.observe(scrollElement);
    applyFitZoom();
    return () => observer.disconnect();
  }, [baselineSize.height, baselineSize.width, fitMode, scrollElement]);

  const scrollToPage = (nextPage) => {
    const safePage = clamp(Number(nextPage) || 1, 1, Math.max(numPages, 1));
    scrollElement
      ?.querySelector(`[data-page-number="${safePage}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPageNumber(safePage);
  };

  const handlePageLoad = (pdfPage) => {
    const viewport = pdfPage.getViewport({ scale: 1 });
    setPageSizes((current) => {
      const existing = current[pdfPage.pageNumber];
      if (existing?.width === viewport.width && existing?.height === viewport.height)
        return current;
      return {
        ...current,
        [pdfPage.pageNumber]: { width: viewport.width, height: viewport.height },
      };
    });
  };

  if (!file) {
    return (
      <div className="fax-viewer-empty">
        <strong>Preview unavailable</strong>
        <span>The fax PDF has not been generated yet.</span>
        {onRetry && (
          <button type="button" className="btn btn-primary height-25" onClick={onRetry}>
            Refresh Fax
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="fax-pdf-viewer">
      <div className="fax-pdf-viewer-content">
        <PdfThumbnailRail scrollRef={setRailScrollElement} portalRef={setRailPortalElement} />
        <div className="fax-pdf-scroll" ref={setScrollElement}>
          <Document
            file={file}
            options={PDF_LOAD_OPTIONS}
            loading={<div className="fax-viewer-loading">Loading fax preview…</div>}
            onLoadSuccess={(pdf) => {
              setNumPages(pdf?.numPages || 0);
              onLoadError?.("");
            }}
            onLoadError={(error) => {
              console.error("Unable to load fax PDF:", error);
              onLoadError?.("The fax preview could not be loaded. Refresh it and try again.");
            }}
          >
            {railPortalElement &&
              createPortal(
                <PdfThumbnailPages
                  numPages={numPages}
                  pageNumbers={pageNumbers}
                  currentPage={pageNumber}
                  scrollRootEl={railScrollElement}
                  onThumbnailClick={scrollToPage}
                  setPageNumber={setPageNumber}
                />,
                railPortalElement
              )}

            {pageNumbers.map((page) => {
              const size = pageSizes[page] || baselineSize;
              return (
                <div
                  key={page}
                  className="fax-pdf-page pdf-page"
                  data-page-number={page}
                  data-window-key={page}
                  style={{ width: size.width * zoom, height: size.height * zoom }}
                >
                  {visiblePages.has(page) && (
                    <Page
                      pageNumber={page}
                      scale={zoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadSuccess={handlePageLoad}
                    />
                  )}
                </div>
              );
            })}
          </Document>
        </div>
      </div>

      <PdfNavigation
        showSearch={false}
        totalPages={numPages}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        onZoomChange={handleZoomChange}
        zoomScale={zoom}
        scrollPageToView={scrollToPage}
        isChatOpen={false}
      />
    </div>
  );
};

export default FaxPdfViewer;
